/*
 * BatchGroupDetailModal.jsx — iter187
 *
 * When a user clicks a production row in the Sales Ops Table, this modal
 * opens to show every batch in that (date · product · client) group with:
 *
 *   1. Aggregate KPI strip — total expected, total actual, total wastage,
 *      avg loss %, and the union of every RM lot consumed across all
 *      sibling batches.
 *   2. Per-batch card — batch_id, status, expected/actual/wastage,
 *      and the full materials_consumed[] list (material name, lot_no, qty,
 *      unit). Click the pencil to open the existing TimingEditor for that
 *      batch and edit output/wastage/rework/timings inline.
 *
 * This is the "click 6 batches, see ingredients, edit them" surface the
 * operator asked for on 2026-06-03.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Factory, Package, AlertTriangle, Beaker, Edit3, ChevronDown, ChevronRight,
  CheckCircle2, Clock, RotateCw,
} from 'lucide-react';
import TimingEditor from './production/TimingEditor';
import { UnlottedBadge, BatchStockHistory, ConsumptionRowActions, isUnlotted } from './production/BatchStockTools';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CLS = {
  'Planned':       'bg-zinc-800 text-zinc-300 border-zinc-700',
  'In Production': 'bg-orange-900/40 text-orange-200 border-orange-700',
  'Packing':       'bg-amber-900/40 text-amber-200 border-amber-700',
  'QC':            'bg-violet-900/40 text-violet-200 border-violet-700',
  'On Hold':       'bg-red-900/30 text-red-300 border-red-800',
  'Completed':     'bg-green-900/40 text-green-300 border-green-700',
};

export default function BatchGroupDetailModal({ group, dayDate, onClose, isAdmin }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [editing, setEditing] = useState(null);

  const loadBatches = useCallback(async () => {
    if (!group?.batch_ids?.length) { setBatches([]); setLoading(false); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        group.batch_ids.map(id =>
          axios.get(`${API}/production/batches/${id}`, { withCredentials: true, bypassCache: true })
            .then(r => r.data)
            .catch(() => null)
        )
      );
      const ok = results.filter(Boolean);
      setBatches(ok);
    } catch (e) {
      toast.error('Failed to load batch details');
    } finally {
      setLoading(false);
    }
  }, [group]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // Auto-expand the single batch when a group of 1 finishes loading.
  // Memo derives whether to auto-include the batch in the expanded set
  // without triggering a setState inside useEffect (React 19 strict rule).
  const effectiveExpanded = useMemo(() => {
    if (batches.length === 1 && !expanded.has(batches[0].id)) {
      return new Set([batches[0].id]);
    }
    return expanded;
  }, [batches, expanded]);

  const toggle = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Aggregates across all batches in the group ──
  const agg = useMemo(() => {
    let expected = 0, actual = 0, wastage = 0, rework = 0, plannedBom = 0;
    const lotSet = new Set();
    const materialMap = new Map(); // name → {qty, unit, lots:Set}
    batches.forEach(b => {
      // iter193 — Mirror the backend math: expected = batch_size + rework
      // (or expected_output_override if set), so the modal's KPI tiles
      // match what the Ops row says.
      const planned = parseFloat(b.batch_size_kg || 0)
        || Math.max(0, parseFloat(b.total_output_kg || 0) - parseFloat(b.rework_added_kg || 0));
      let reworkB = parseFloat(b.rework_added_kg || 0);
      if (!reworkB) {
        reworkB = (b.rework_materials || []).reduce((s, rm) =>
          s + (parseFloat(rm.qty_kg || rm.quantity || rm.qty || 0) || 0), 0);
      }
      const override = b.expected_output_override;
      const exp = (override != null && override !== '' && override !== 0)
        ? parseFloat(override)
        : planned + reworkB;
      const act = parseFloat(b.actual_output_kg || 0);
      const wasteRecorded = parseFloat(b.wastage_kg || 0);
      const wasteDerived  = (act > 0 && exp > 0) ? Math.max(0, exp - act) : 0;
      plannedBom += planned;
      rework     += reworkB;
      expected   += exp;
      actual     += act;
      wastage    += wasteRecorded > 0 ? wasteRecorded : wasteDerived;
      (b.materials_consumed || []).forEach(mc => {
        if (mc.lot_no) lotSet.add(mc.lot_no);
        const name = mc.material_name || mc.name || '—';
        const cur = materialMap.get(name) || { qty: 0, unit: mc.unit || 'kg', lots: new Set() };
        cur.qty += parseFloat(mc.quantity_used || mc.quantity || 0);
        if (mc.lot_no) cur.lots.add(mc.lot_no);
        materialMap.set(name, cur);
      });
    });
    const lossPct = expected > 0 ? (Math.max(0, expected - actual) / expected * 100) : 0;
    return {
      expected:   round(expected),
      plannedBom: round(plannedBom),
      rework:     round(rework),
      actual:     round(actual),
      wastage:    round(wastage),
      lossPct:    round(lossPct),
      lots: Array.from(lotSet).sort(),
      materials: Array.from(materialMap.entries()).map(([name, v]) => ({
        name, qty: round(v.qty), unit: v.unit, lots: Array.from(v.lots).sort(),
      })),
    };
  }, [batches]);

  const onBatchSaved = async (id, patch) => {
    try {
      await axios.patch(`${API}/production/batches/${id}/details`, patch, { withCredentials: true });
      toast.success('Batch updated');
      setEditing(null);
      await loadBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="batch-group-modal">
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <Factory className="text-orange-400 shrink-0" size={20}/>
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-zinc-50 text-sm truncate">
                {group.product_name} · {group.batches_count} batch{group.batches_count > 1 ? 'es' : ''}
              </h3>
              <p className="text-zinc-500 text-[11px] mt-0.5">
                For <span className="text-sky-300">{group.for_client || 'General Stock'}</span>
                {dayDate && <> · <span className="font-mono">{dayDate}</span></>}
                {group.line_code && <> · Line <span className="font-mono">{group.line_code}</span></>}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="batch-group-modal-close" className="text-zinc-500 hover:text-zinc-200">
            <X size={18}/>
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
            <RotateCw size={14} className="animate-spin"/> Loading {group.batch_ids?.length || 0} batches…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Aggregate KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiTile
                icon={<Package size={13} className="text-emerald-400"/>}
                label="Total Expected"
                value={`${agg.expected} kg`}
                sublabel={agg.rework > 0 ? `Plan ${agg.plannedBom} + Rework ${agg.rework}` : null}
                testid="bg-kpi-expected"
              />
              <KpiTile icon={<CheckCircle2 size={13} className="text-sky-400"/>} label="Total Actual" value={`${agg.actual} kg`} testid="bg-kpi-actual" />
              <KpiTile icon={<AlertTriangle size={13} className="text-amber-400"/>} label="Wastage" value={`${agg.wastage} kg`} testid="bg-kpi-wastage" />
              <KpiTile icon={<AlertTriangle size={13} className={agg.lossPct > 5 ? 'text-red-400' : 'text-zinc-400'}/>} label="Avg Loss %" value={`${agg.lossPct}%`} testid="bg-kpi-loss" hot={agg.lossPct > 5} />
            </div>

            {/* Aggregate Materials Summary */}
            {agg.materials.length > 0 && (
              <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 mb-2">
                  <Beaker size={11} className="text-amber-400"/> Combined Ingredients Across All Batches
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {agg.materials.map(m => (
                    <div key={m.name} className="flex items-start gap-2 text-[11px] bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5" data-testid={`bg-agg-mat-${m.name.replace(/\s+/g, '-')}`}>
                      <span className="text-zinc-100 font-medium flex-1 truncate">{m.name}</span>
                      <span className="font-mono text-amber-300 text-[11px]">{m.qty} {m.unit}</span>
                      {m.lots.length > 0 && (
                        <div className="flex gap-0.5 flex-wrap max-w-[140px]">
                          {m.lots.slice(0, 3).map(l => (
                            <span key={l} className="text-[9px] font-mono bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1 rounded">{l}</span>
                          ))}
                          {m.lots.length > 3 && <span className="text-[9px] text-zinc-500">+{m.lots.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {agg.lots.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] text-zinc-500">All Lots Consumed:</span>
                    {agg.lots.map(l => (
                      <span key={l} className="text-[10px] font-mono bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded">{l}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Per-batch list */}
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                Per-Batch Breakdown ({batches.length})
              </div>
              {batches.length === 0 ? (
                <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-6 text-center text-zinc-600 text-sm">
                  No batches found. They may have been deleted.
                </div>
              ) : (
                batches.map(b => (
                  <BatchCard
                    key={b.id}
                    batch={b}
                    expanded={effectiveExpanded.has(b.id)}
                    onToggle={() => toggle(b.id)}
                    onEdit={() => setEditing(b)}
                    isAdmin={isAdmin}
                    onChanged={loadBatches}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit overlay (TimingEditor) */}
      {editing && (
        <TimingEditor
          batch={editing}
          onClose={() => setEditing(null)}
          onSave={onBatchSaved}
          isAdmin={isAdmin}
          onRenamed={() => loadBatches()}
        />
      )}
    </div>
  );
}

const round = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

function KpiTile({ icon, label, value, sublabel, testid, hot }) {
  return (
    <div className={`bg-zinc-950/60 border rounded-lg px-3 py-2 ${hot ? 'border-red-700/60' : 'border-zinc-800'}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">{icon}{label}</div>
      <div className={`text-base font-bold mt-0.5 font-mono ${hot ? 'text-red-300' : 'text-zinc-100'}`}>{value}</div>
      {sublabel && <div className="text-[9px] text-zinc-500 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function BatchCard({ batch, expanded, onToggle, onEdit, isAdmin, onChanged }) {
  const statusCls = STATUS_CLS[batch.status] || STATUS_CLS['Planned'];
  // iter193 — Mirror backend: expected = base + rework (or override), and
  // wastage falls back to (expected - actual) when the persisted wastage_kg
  // is missing.
  const planned = parseFloat(batch.batch_size_kg || 0)
    || Math.max(0, parseFloat(batch.total_output_kg || 0) - parseFloat(batch.rework_added_kg || 0));
  let reworkB = parseFloat(batch.rework_added_kg || 0);
  if (!reworkB) {
    reworkB = (batch.rework_materials || []).reduce((s, rm) =>
      s + (parseFloat(rm.qty_kg || rm.quantity || rm.qty || 0) || 0), 0);
  }
  const override = batch.expected_output_override;
  const expected = (override != null && override !== '' && override !== 0)
    ? parseFloat(override)
    : planned + reworkB;
  const actual   = parseFloat(batch.actual_output_kg || 0);
  const wasteRec = parseFloat(batch.wastage_kg || 0);
  const wastage  = wasteRec > 0 ? wasteRec : ((actual > 0 && expected > 0) ? Math.max(0, expected - actual) : 0);
  const lossPct  = (actual > 0 && expected > 0) ? round(Math.max(0, expected - actual) / expected * 100) : 0;
  const lossRed  = lossPct > 5;
  const mats = batch.materials_consumed || [];
  const rework = (batch.rework_materials || []).filter(rm => rm.name);

  return (
    <div className={`bg-zinc-950/60 border rounded-xl overflow-hidden ${lossRed ? 'border-red-800/50' : 'border-zinc-800'}`} data-testid={`bg-batch-card-${batch.id}`}>
      <button
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-zinc-900/80 transition-colors"
        data-testid={`bg-batch-toggle-${batch.id}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {expanded ? <ChevronDown size={14} className="text-zinc-500"/> : <ChevronRight size={14} className="text-zinc-500"/>}
          <span className="font-mono text-sm font-semibold text-sky-300">{batch.id}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusCls}`}>{batch.status}</span>
          {batch.sub_batch_letter && (
            <span className="text-[9px] text-zinc-500 font-mono">#{batch.sub_batch_letter}</span>
          )}
          <UnlottedBadge batch={batch} />
          {batch.production_date && (
            <span className="text-[10px] text-zinc-500 font-mono ml-auto">{batch.production_date}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] shrink-0">
          <span><span className="text-zinc-500">Out</span> <span className="font-mono text-emerald-300">{actual || expected} kg</span></span>
          <span title="Loss"><span className="text-zinc-500">Loss</span> <span className={`font-mono font-semibold ${lossRed ? 'text-red-300' : 'text-zinc-200'}`}>{lossPct}%</span></span>
          <span onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-amber-400 cursor-pointer"
            data-testid={`bg-batch-edit-${batch.id}`}
            title="Edit batch (output, wastage, timings, rework)"
          >
            <Edit3 size={12}/>
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-zinc-800/60">
          {/* Quick stats grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-[10px]">
            <Stat label="Expected" value={`${round(expected)} kg`} />
            <Stat label="Actual" value={`${round(actual)} kg`} />
            <Stat label="Wastage" value={`${round(wastage)} kg`} red={lossRed}/>
            <Stat label="Loss %" value={`${lossPct}%`} red={lossRed}/>
            <Stat label="Batch Size" value={`${round(batch.batch_size_kg)} kg`} />
            {reworkB > 0 && (
              <Stat label="Rework Added" value={`${round(reworkB)} kg`} />
            )}
          </div>

          {/* Ingredients table */}
          <div>
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mb-1">
              <Beaker size={10} className="text-amber-400"/> Ingredients ({mats.length})
            </div>
            {mats.length === 0 ? (
              <div className="text-[11px] text-zinc-600 italic px-2 py-1">No materials recorded.</div>
            ) : (
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-md overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-zinc-950">
                    <tr className="text-[10px] text-zinc-500">
                      <th className="px-2 py-1 text-left font-medium">Material</th>
                      <th className="px-2 py-1 text-left font-medium">Lot No</th>
                      <th className="px-2 py-1 text-right font-medium">Qty Used</th>
                      <th className="px-2 py-1 text-left font-medium">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mats.map((mc, i) => (
                      <tr key={mc.row_id || i} className={`border-t border-zinc-800/50 ${isUnlotted(mc) ? 'bg-amber-950/20' : ''}`} data-testid={`bg-mat-row-${batch.id}-${i}`}>
                        <td className="px-2 py-1.5 text-zinc-200">{mc.material_name || mc.name || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            {isUnlotted(mc) ? (
                              <span className="font-mono text-amber-300 border border-dashed border-amber-500/70 px-1.5 py-0.5 rounded text-[10px]" data-testid={`unlotted-row-${batch.id}-${mc.row_id || i}`}>UNLOTTED</span>
                            ) : mc.lot_no ? (
                              <span className="font-mono text-amber-300 bg-amber-900/30 border border-amber-700/40 px-1.5 py-0.5 rounded text-[10px]">{mc.lot_no}</span>
                            ) : <span className="text-zinc-600">—</span>}
                            <ConsumptionRowActions batch={batch} mc={mc} isAdmin={isAdmin} onChanged={onChanged} />
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-zinc-100">{round(mc.quantity_used || mc.quantity)}</td>
                        <td className="px-2 py-1.5 text-zinc-500">{mc.unit || 'kg'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <BatchStockHistory batch={batch} isAdmin={isAdmin} onChanged={onChanged} />

          {/* Rework materials (if any) */}
          {rework.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mb-1">
                <RotateCw size={10} className="text-amber-400"/> Rework / Old Material ({rework.length})
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {rework.map((rm, i) => (
                  <span key={i} className="text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-2 py-1 rounded">
                    {rm.name} · <span className="font-mono">{round(rm.qty_kg)} kg</span>
                    {rm.mfg_date && <span className="text-amber-500/60 ml-1">({rm.mfg_date})</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Remark */}
          {batch.remark && (
            <div className="text-[10px] text-zinc-400 italic px-2 py-1 bg-zinc-900 border border-zinc-800 rounded">
              <Clock size={9} className="inline mr-1"/> {batch.remark}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, red }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-[12px] font-mono font-semibold ${red ? 'text-red-300' : 'text-zinc-100'}`}>{value}</div>
    </div>
  );
}
